import React, { useState, useRef } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  FileDownload,
  TableChart,
  Analytics,
  KeyboardArrowDown,
} from '@mui/icons-material';
import './billingExcelDownloadButton.css';
import { ReactComponent as Excel } from '../../assets/ICONS/excelFilled.svg';

export interface BillingExcelDownloadOption {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface ExcelDownloadButtonProps {
  handleExcelDownload: (reportType: string) => void;
  options?: BillingExcelDownloadOption[];
  disabled?: boolean;
  isDownloading: boolean;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
}

const defaultOptions: BillingExcelDownloadOption[] = [
  {
    id: 'summary',
    label: 'Summary Sheet',
    description: 'Download summary data',
    icon: <Analytics />,
  },
  {
    id: 'detailed',
    label: 'Detail Sheet',
    description: 'Download detailed breakdown',
    icon: <TableChart />,
  },
];

const BillingExcelDownloadButton: React.FC<ExcelDownloadButtonProps> = ({
  handleExcelDownload,
  options = defaultOptions,
  disabled = false,
  className = '',
  size = 'small',
  variant = 'contained',
  color = 'primary',
  isDownloading = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingOption, setDownloadingOption] = useState<string | null>(
    null
  );
  // const [reportType,setReportType] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isMenuOpen = Boolean(anchorEl);

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled && !isDownloading) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // const handleDownload = (optionId: string) => {
  //   setIsDownloading(true);
  //   setDownloadingOption(optionId);
  //   setAnchorEl(null);

  //   // Call the onDownload handler passed from parent
  //   onDownload(optionId);
  // };

  const getButtonContent = () => {
    if (isDownloading) {
      return (
        <>
          <CircularProgress
            size={16}
            className="excelDownloadButton__loadingIcon"
          />
          <span className="excelDownloadButton__loadingText">
            Downloading...
          </span>
        </>
      );
    }

    return (
      <>
        <Excel
          style={{ color: 'green' }}
          className="excelDownloadButton__icon"
        />
        <span className="excelDownloadButton__text">Export to Excel</span>
        <KeyboardArrowDown className="excelDownloadButton__arrowIcon" />
      </>
    );
  };

  return (
    <div className={`excelDownloadButton ${className}`}>
      <Tooltip
        title={disabled ? 'Export not available' : 'Export data to Excel'}
        placement="top"
      >
        <span>
          <Button
            ref={buttonRef}
            variant={variant}
            color={color}
            size={size}
            onClick={handleButtonClick}
            disabled={disabled || isDownloading}
            className={`excelDownloadButton__mainButton ${
              isDownloading ? 'excelDownloadButton__mainButton--loading' : ''
            }`}
            aria-controls={isMenuOpen ? 'excel-download-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? 'true' : undefined}
          >
            {getButtonContent()}
          </Button>
        </span>
      </Tooltip>

      <Menu
        id="excel-download-menu"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'excel-download-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        className="excelDownloadButton__menu"
        PaperProps={{
          className: 'excelDownloadButton__menuPaper',
        }}
      >
        <div className="excelDownloadButton__menuHeader">
          <FileDownload className="excelDownloadButton__menuHeaderIcon" />
          <span className="excelDownloadButton__menuHeaderText">
            Choose Export Type
          </span>
        </div>
        <Divider className="excelDownloadButton__divider" />

        {options.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleExcelDownload(option.id)}
            disabled={option.disabled || isDownloading}
            className={`excelDownloadButton__menuItem ${
              downloadingOption === option.id
                ? 'excelDownloadButton__menuItem--downloading'
                : ''
            }`}
          >
            <ListItemIcon className="excelDownloadButton__menuItemIcon">
              {downloadingOption === option.id ? (
                <CircularProgress size={20} />
              ) : (
                option.icon || <TableChart />
              )}
            </ListItemIcon>
            <ListItemText
              primary={option.label}
              secondary={option.description}
              className="excelDownloadButton__menuItemText"
              primaryTypographyProps={{
                className: 'excelDownloadButton__menuItemPrimary',
              }}
              secondaryTypographyProps={{
                className: 'excelDownloadButton__menuItemSecondary',
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default BillingExcelDownloadButton;
